import styled from "@emotion/styled"
import { PButton, PHeading, PText } from "@porsche-design-system/components-react"
import { useCallback, useContext, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { commonMessages } from "src/common/messages/common.messages"
import { ButtonGroup } from "src/components"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useExportDisclaimerText } from "src/hooks"

const messages = defineMessages({
	headline: {
		defaultMessage: "Export Vehicles",
		id: "exportDisclaimer.headline",
	},
	subline: {
		defaultMessage: "Disclaimer",
		id: "exportDisclaimer.subline",
	},
})

interface ExportDisclaimerModalProps {
	onConfirm: () => Promise<void>
}

type Props = ModalProps<ExportDisclaimerModalProps>

export const ExportDisclaimerModal = withErrorHandler(({ id, modalProps }: Props) => {
	const { onConfirm } = modalProps
	const [isLoading, setIsLoading] = useState(false)
	const { formatMessage } = useIntl()

	const { hideModal } = useContext(ModalContext)

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const onSubmit = () => {
		setIsLoading(true)
		onConfirm().finally(() => {
			setIsLoading(false)
			hide()
		})
	}

	const disclaimerText = useExportDisclaimerText()

	return (
		<Modal id={id} heading={formatMessage(messages.headline)}>
			<PHeading size="sm" weight="semibold">
				<FormattedMessage {...messages.subline} />
			</PHeading>
			<StyledText>{disclaimerText}</StyledText>

			<ButtonGroup>
				<PButton loading={isLoading} onClick={onSubmit}>
					<FormattedMessage {...commonMessages.confirm} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={hide}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</Modal>
	)
})

const StyledText = styled(PText)`
	max-width: 700px;
`
